/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
	Alert,
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import {
	FormEvent,
	ReactElement,
	useEffect,
	useMemo,
	useState,
	forwardRef,
	useImperativeHandle,
	RefObject,
} from 'react';
import { TAX_CONSIDERATION_DEFAULT_COPY, TAX_EXEMPTIONS_DEFAULT_COPY } from '../../ips.constants';
import { IpsData, IpsFormData } from '@/app/api/flows/ips.types';
import FileInput, { FileInputRef } from '@/shared/components/file-input/FileInput';
import TextEditor from '../text-editor';
import { FileStatus, useFileUpload } from '@/shared/hooks/use-file-upload';

interface IpsFormProps {
	defaultValues: IpsFormData & IpsData;
	submitDisabled?: boolean;
	onSubmit: (ipsFormData: IpsFormData) => void;
}

export interface IpsFormRef {
	resetFileState: () => void;
	fileRemoved: () => void;
}

const IpsForm = forwardRef<IpsFormRef, IpsFormProps>(
	({ onSubmit, defaultValues, submitDisabled = false }, ref): ReactElement => {
		const [ipsFormData, setIpsFormData] = useState<IpsFormData & IpsData>(defaultValues);
		const [isTaxConsiderationAlertOpen, setIsTaxConsiderationAlertOpen] = useState(false);
		const [isTaxExemptionsAlertOpen, setIsTaxExemptionsAlertOpen] = useState(false);
		const [isLiquidityRequirementsDetailsShown, setIsLiquidityRequirementsDetailsShown] =
			useState(false);
		const [ipsFooter, setIpsFooter] = useState('');
		const { fileInputRef, file, status, onFileChange, setFile, setStatus, onFileRemoved } =
			useFileUpload();

		const resetFileState = () => {
			setFile(undefined);
			setStatus(undefined);
		};

		const fileRemoved = () => {
			onFileRemoved();
			(fileInputRef.current as unknown as FileInputRef)?.resetPreview();
		};

		useImperativeHandle(ref, () => ({
			resetFileState,
			fileRemoved,
		}));

		useEffect(() => {
			setIpsFormData(defaultValues);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [defaultValues]);

		useEffect(() => {
			setIsTaxConsiderationAlertOpen(
				ipsFormData.taxConsideration !== TAX_CONSIDERATION_DEFAULT_COPY
			);
		}, [ipsFormData.taxConsideration]);

		useEffect(() => {
			setIsTaxExemptionsAlertOpen(ipsFormData.taxExemptions !== TAX_EXEMPTIONS_DEFAULT_COPY);
		}, [ipsFormData.taxExemptions]);

		useEffect(() => {
			setIsLiquidityRequirementsDetailsShown(
				ipsFormData.timeHorizon === ipsFormData.timeHorizonOptions[0]?.value
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [ipsFormData.timeHorizon]);

		const isDisabled = useMemo(() => {
			return (
				!ipsFormData.riskProfile ||
				!ipsFormData.timeHorizon ||
				(isLiquidityRequirementsDetailsShown && !ipsFormData.liquidityRequirementsDetails) ||
				!ipsFormData.taxConsideration ||
				!ipsFormData.taxExemptions
			);
		}, [ipsFormData, isLiquidityRequirementsDetailsShown]);

		const submit = (event: FormEvent) => {
			event.preventDefault();
			onSubmit({
				...ipsFormData,
				...(isLiquidityRequirementsDetailsShown
					? { liquidityRequirementsDetails: ipsFormData.liquidityRequirementsDetails }
					: { liquidityRequirementsDetails: '' }),
				ipsFooter,
				ipsLogo: file || undefined,
			});
		};

		const handleChange = (event: SelectChangeEvent) => {
			setIpsFormData({
				...ipsFormData,
				[event.target.name]: event.target.value,
			});
		};

		const isContentEmpty = (content: string): boolean => {
			// Remove HTML tags and check if there's actual text content
			const textContent = content.replace(/<[^>]*>/g, '').trim();
			return textContent === '';
		};

		return (
			<form className="flex flex-col gap-6" onSubmit={submit}>
				<div>
					<Typography variant="h6" sx={{ marginBottom: '4px' }}>
						IPS Setup
					</Typography>
					<Typography component="p" sx={{ fontSize: '14px', color: '#000000', opacity: '0.6' }}>
						Upload your custom branding here. If you prefer not to, the default branding will be
						Concurrent. You can also modify the text in the footer of the IPS below.
					</Typography>
				</div>
				<div style={{ maxWidth: '140px', marginTop: '1.5rem' }}>
					<FileInput
						id="upload-logo"
						name="upload-logo"
						buttonText="Upload Logo"
						accept=".png, .jpg, .jpeg"
						variant="outlined"
						showPreview
						ref={fileInputRef as RefObject<FileInputRef>}
						onChange={onFileChange}
						disabled={file && status !== FileStatus.LOADED}
					/>
				</div>
				<Box
					sx={{
						marginTop: '1.5rem',
						marginBottom: '1.5rem',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-start',
						gap: '1rem',
					}}
				>
					<TextEditor
						value={ipsFormData.ipsFooter}
						onChange={setIpsFooter}
						placeholder="Footer text"
						style={{ width: '100%' }}
					/>
					<Button
						variant="outlined"
						onClick={() => setIpsFooter('')}
						disabled={isContentEmpty(ipsFooter)}
					>
						Reset
					</Button>
				</Box>
				<FormControl fullWidth variant="filled">
					<InputLabel id="risk-profile-select-label">Risk Profile</InputLabel>
					<Select
						labelId="risk-profile-select-label"
						id="risk-profile-select"
						name="riskProfile"
						value={ipsFormData.riskProfile}
						onChange={handleChange}
					>
						{ipsFormData.riskProfileOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{isTaxConsiderationAlertOpen && (
					<Alert variant="filled" severity="info" sx={{ mt: 3 }}>
						Default copy provided for tax related fields has been preapproved by compliance. If
						custom text is used, please submit text to compliance for review.
					</Alert>
				)}
				<TextField
					fullWidth
					id="tax-consideration"
					label="Tax Consideration"
					variant="filled"
					value={ipsFormData.taxConsideration}
					onChange={(e) => handleChange(e as SelectChangeEvent)}
					name="taxConsideration"
					sx={{ mt: 2 }}
				/>

				{isTaxExemptionsAlertOpen && (
					<Alert variant="filled" severity="info" sx={{ mt: 3 }}>
						Default copy provided for tax related fields has been preapproved by compliance. If
						custom text is used, please submit text to compliance for review.
					</Alert>
				)}
				<TextField
					fullWidth
					id="tax-exemptions"
					label="Tax Exemptions"
					variant="filled"
					value={ipsFormData.taxExemptions}
					onChange={(e) => handleChange(e as SelectChangeEvent)}
					name="taxExemptions"
					sx={{ mt: 2 }}
				/>

				<FormControl fullWidth variant="filled" sx={{ mt: 3 }}>
					<InputLabel id="time-horizon-select-label">Time Horizon</InputLabel>
					<Select
						labelId="time-horizon-select-label"
						id="time-horizon-select"
						name="timeHorizon"
						value={ipsFormData.timeHorizon}
						onChange={handleChange}
					>
						{ipsFormData.timeHorizonOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{isLiquidityRequirementsDetailsShown && (
					<>
						<Alert variant="filled" severity="info" sx={{ mt: 3 }}>
							If the &quot;Less than 2 years&quot; time horizon was selected, specific liquidity
							requirement details are needed.
						</Alert>
						<TextField
							fullWidth
							id="liquidity-requirements-details"
							label="Liquidity Requirement Details"
							variant="filled"
							value={ipsFormData.liquidityRequirementsDetails}
							onChange={(e) => handleChange(e as SelectChangeEvent)}
							name="liquidityRequirementsDetails"
							sx={{ mt: 2 }}
						/>
					</>
				)}

				<Button
					sx={{ mt: 3 }}
					variant="contained"
					color="primary"
					type="submit"
					disabled={isDisabled || submitDisabled}
				>
					Generate IPS
				</Button>
			</form>
		);
	}
);

IpsForm.displayName = 'IpsForm';

export default IpsForm;
